/**
 * The Shipping tab, the Shipping page and their drawer share one stylesheet (#717). `shp-` is a naming
 * convention, not a scope: this is an ordinary global stylesheet, live while any of them is mounted.
 */
const css = `
  .shp-pad { padding: 10px var(--ew-s-4); margin: 0; }
  .shp-muted { color: var(--ew-n-500); }
  .shp-small { font-size: 12px; }
  .shp-error { color: var(--ew-danger-700); background: var(--ew-danger-50); border-radius: var(--ew-r-sm); padding: 8px 10px; margin: 0; }
  .shp-ok { color: var(--ew-green-900); background: var(--ew-green-50); border-radius: var(--ew-r-sm); padding: 8px 10px; margin: 0 0 var(--ew-s-3); }
  /* The side padding is on the header, not the heading, so a button that wraps onto its own line on a phone lines up
     with the heading instead of sitting against the card's edge. */
  .shp-hd { display: flex; align-items: center; gap: 12px; padding: 0 var(--ew-s-4); border-bottom: 1px solid var(--ew-n-200); flex-wrap: wrap; }
  .shp-hd .pw-card-hd { flex: 1; border: 0; padding-left: 0; padding-right: 0; }
  .shp-hd > button { margin-bottom: 12px; }
  .shp-title { flex: 1; margin: 0; font-weight: 700; font-size: 18px; }
  .shp-top { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }

  /* The list: one request per row, as it is read on a phone and at a desk */
  /* ⚠️ The row stacks by the LIST's width, not the window's. On a job the left rail takes about 230px, so a window that
     is wide enough for five columns on the Shipping page leaves the job's tab far too narrow for them. */
  .shp-list { list-style: none; margin: 0; padding: 0; container-type: inline-size; }
  .shp-row {
    display: grid; grid-template-columns: minmax(0, 2.2fr) minmax(0, 1.5fr) 110px 130px minmax(0, 1fr);
    gap: 12px; align-items: center; padding: 10px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100);
    font-size: var(--ew-fs-body-sm); width: 100%; text-align: left; background: none; border-right: 0; border-top: 0;
    cursor: pointer; font-family: inherit; color: inherit;
    /* Every row reserves the Expedited marker's width, so its text lines up with the others. */
    border-left: 3px solid transparent;
  }
  .shp-row:hover { background: var(--ew-n-50); }
  .shp-row:focus-visible { outline: 2px solid var(--ew-green); outline-offset: -2px; }
  .shp-row.expedited { border-left-color: var(--ew-danger); }
  .shp-main { display: grid; gap: 3px; min-width: 0; }
  .shp-badges { display: flex; gap: 6px; flex-wrap: wrap; }
  .shp-items { font-weight: var(--ew-fw-semibold); color: var(--ew-dark); overflow-wrap: anywhere; }
  .shp-route { overflow-wrap: anywhere; }
  @container (max-width: 700px) { .shp-row { grid-template-columns: 1fr; gap: 3px; } }

  /* The Shipping page's filter bar and its card */
  .shp-filters { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: 16px; padding: 12px 16px; background: #fff; border-radius: var(--ew-r-lg); border: 1px solid var(--ew-n-200); }
  .shp-filters input[type="search"], .shp-filters select { font: inherit; padding: 6px 10px; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-sm); background: #fff; min-width: 0; }
  .shp-filters input[type="search"] { flex: 1 1 240px; }
  .shp-check { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: var(--ew-n-600); cursor: pointer; user-select: none; }
  .shp-hidden-count { background: var(--ew-n-50); color: var(--ew-n-600); border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-pill); padding: 1px 8px; font-size: 11px; font-weight: 600; }
  .shp-card { background: #fff; border-radius: var(--ew-r-lg); border: 1px solid var(--ew-n-200); overflow: hidden; }

  /* The drawer */
  .shp-scrim { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.25); z-index: 200; }
  .shp-drawer {
    position: fixed; top: 0; right: 0; bottom: 0; width: min(560px, 100vw); background: #fff; z-index: 201;
    overflow-y: auto; box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12); display: flex; flex-direction: column;
  }
  .shp-drawer-hd {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; padding: 14px 18px;
    border-bottom: 1px solid var(--ew-n-200); position: sticky; top: 0; background: #fff; z-index: 1; flex-wrap: wrap;
  }
  .shp-drawer-title { font-weight: 700; font-size: 15px; }
  .shp-drawer-body { padding: 4px 18px 32px; display: grid; gap: 14px; }
  .shp-section {
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase;
    color: var(--ew-green-700); border-bottom: 1px solid var(--ew-n-200); padding: 18px 0 6px; margin-bottom: 8px;
  }
  .shp-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 14px; }
  @media (max-width: 520px) { .shp-grid { grid-template-columns: 1fr; } }
  .shp-field { display: grid; gap: 4px; font-size: 13px; align-content: start; min-width: 0; }
  .shp-field > span { font-weight: var(--ew-fw-medium); color: var(--ew-n-700); }
  .shp-field input, .shp-field select, .shp-field textarea {
    font: inherit; padding: 7px 9px; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-sm); background: #fff; min-width: 0; width: 100%;
  }
  .shp-field textarea { min-height: 64px; resize: vertical; }
  .shp-field input:disabled, .shp-field select:disabled, .shp-field textarea:disabled { background: var(--ew-n-50); color: var(--ew-n-700); }
  .shp-hint { font-size: 12px; color: var(--ew-n-500); font-weight: var(--ew-fw-regular); }
  .shp-job { padding: 7px 9px; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-sm); background: var(--ew-n-50); overflow-wrap: anywhere; }
  .shp-seg { display: flex; gap: 8px; flex-wrap: wrap; }
  .shp-seg label { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-sm); cursor: pointer; font-size: 13px; background: #fff; }
  .shp-seg label.on { border-color: var(--ew-green); background: var(--ew-green-50); color: var(--ew-green-900); font-weight: var(--ew-fw-semibold); }
  .shp-seg input { width: auto; margin: 0; }
  .shp-bar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  /* No padding here: the body's own (.shp-drawer-body) has to win, and this selector is the more specific. */
  .shp-drawer fieldset { border: 0; margin: 0; min-width: 0; }
`;

export function ShippingStyles() {
  return <style>{css}</style>;
}
